import React, { useEffect, useRef, useState } from "react";
import { DateTime } from "luxon";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

export const SECTION_LABEL_SX = {
  display: "block",
  fontWeight: 700,
  letterSpacing: "0.1em",
  fontSize: "0.7rem",
  color: "text.secondary",
  mb: 1,
  textTransform: "uppercase"
};

// Headings inside the slot list ("Suggested for you", "Morning", "Afternoon"…).
// Same type as SECTION_LABEL_SX but in the club's theme colour, so the sections
// of the list read as one family and stay distinct from the grey filter labels.
export const SLOT_SECTION_LABEL_SX = {
  ...SECTION_LABEL_SX,
  color: "primary.main"
};

export const CARD_SX = {
  p: 3,
  borderRadius: 4,
  boxShadow: "0 4px 20px rgba(26,26,26,0.04)",
  border: "1px solid",
  borderColor: "divider"
};

// Time-of-day buckets used to segment the slot list (mobile = vertical sections,
// desktop = grid rows) so the day is scannable instead of one long wall of times.
// Night exists so clubs running past midnight don't get 00:00–03:00 slots filed
// under "Morning"; consumers only render non-empty buckets, so it costs nothing
// for everyone else.
export const TIME_BUCKETS = [
  { id: "night", label: "Night", test: (h) => h < 6 },
  { id: "morning", label: "Morning", test: (h) => h >= 6 && h < 12 },
  { id: "afternoon", label: "Afternoon", test: (h) => h >= 12 && h < 17 },
  { id: "evening", label: "Evening", test: (h) => h >= 17 }
];

export const bucketIdOf = (time) => {
  const h = parseInt(String(time).split(":")[0], 10) || 0;
  return (TIME_BUCKETS.find((b) => b.test(h)) || TIME_BUCKETS[0]).id;
};

// Build the 7 ISO date strings of the week beginning at `weekStartISO` (Monday).
export const buildWeekDates = (weekStartISO) => {
  const start = DateTime.fromISO(weekStartISO);
  return Array.from({ length: 7 }, (_, i) => start.plus({ days: i }).toISODate());
};

// Widget booking window (max advance): the last calendar date a client can
// book this service, as an ISO date — null when the service has no window.
// Rolling windows count days+hours from now; calendar windows open whole days.
// Device time approximates the club zone here — the server enforces the real
// cutoff on both availability and booking; this only bounds the date pickers.
export const bookingWindowMaxDate = (service, now = DateTime.now()) => {
  const win = service?.widgetBookingWindow;
  if (!win || typeof win.days !== "number") return null;
  if (win.mode === "calendar") return now.plus({ days: win.days }).toISODate();
  return now.plus({ days: win.days, hours: win.hours || 0 }).toISODate();
};

// Duration filter value meaning "every length the service sells": the
// availability request carries no duration, the server probes each length, and
// a time group then holds slots of several lengths — the client picks the time
// first and the length in the picker (same flow as the home-page suggestions).
// Under gap minimization this is what makes a hidden start read as guidance:
// 5:00 offers 45 or 90 instead of simply vanishing for a 60.
export const ALL_DURATIONS = "all";

// Collapse raw slots into per-time groups. A single time can carry many slots
// (one per court/trainer × location, and one per length under "All
// durations"); the group surfaces the count, its distinct lengths (ascending)
// and a "from" price.
export function groupSlotsByTime(slots) {
  const groups = new Map();
  for (const slot of slots || []) {
    if (!groups.has(slot.time)) {
      groups.set(slot.time, {
        time: slot.time,
        slots: [],
        durations: [],
        minPrice: Number.POSITIVE_INFINITY,
        maxPrice: 0,
        currency: slot.currency
      });
    }
    const g = groups.get(slot.time);
    g.slots.push(slot);
    const d = parseInt(slot.duration, 10);
    if (Number.isInteger(d) && !g.durations.includes(d)) g.durations.push(d);
    const p = parseFloat(slot.price);
    if (p < g.minPrice) g.minPrice = p;
    if (p > g.maxPrice) g.maxPrice = p;
  }
  for (const g of groups.values()) g.durations.sort((a, b) => a - b);
  return Array.from(groups.values()).sort((a, b) => a.time.localeCompare(b.time));
}

// The lengths a time group offers, each with the cheapest price among its
// slots (`isFrom` when they disagree, e.g. across branches). Same shape as the
// service-level duration options so the chips can share a label.
export function groupDurationOptions(group) {
  const byDur = new Map();
  for (const slot of group?.slots || []) {
    const d = parseInt(slot.duration, 10);
    if (!Number.isInteger(d)) continue;
    const p = parseFloat(slot.price);
    const cur = byDur.get(d) || { value: d, label: `${d} min`, min: Number.POSITIVE_INFINITY, max: 0 };
    if (p < cur.min) cur.min = p;
    if (p > cur.max) cur.max = p;
    byDur.set(d, cur);
  }
  return [...byDur.values()]
    .sort((a, b) => a.value - b.value)
    .map(({ value, label, min, max }) => ({ value, label, price: Number.isFinite(min) ? min : null, isFrom: Number.isFinite(min) && max !== min }));
}

// A time group narrowed to one length — what the resource rows and the "Any"
// resolution work on once the client has chosen a duration. A group with one
// length (or no duration given) passes through unchanged.
export function filterGroupByDuration(group, duration) {
  if (!group) return group;
  const d = parseInt(duration, 10);
  if (!Number.isInteger(d) || (group.durations || []).length <= 1) return group;
  const slots = group.slots.filter((s) => parseInt(s.duration, 10) === d);
  if (!slots.length) return group;
  const prices = slots.map((s) => parseFloat(s.price));
  return { ...group, slots, durations: [d], minPrice: Math.min(...prices), maxPrice: Math.max(...prices) };
}

// The length a multi-length picker opens on: 60 when the group offers it,
// else the shortest. Resets whenever the picker is pointed at another group.
export function useGroupDuration(group) {
  const [picked, setPicked] = useState(null);
  useEffect(() => {
    setPicked(null);
  }, [group?.time, group?.slots?.[0]?.startDate]);
  const durations = group?.durations || [];
  const fallback = durations.includes(60) ? 60 : (durations[0] ?? null);
  return [picked != null && durations.includes(picked) ? picked : fallback, setPicked];
}

// Length picker strip for a multi-length time group: one chip per length with
// its price, the active one filled. Rendered by the options popover / sheet
// above the resource rows.
export function DurationChips({ options, value, onChange, currency }) {
  if (!options || options.length <= 1) return null;
  const cur = (currency || "").toUpperCase();
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, px: 0.5 }}>
        Duration
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", px: 0.5 }}>
        {options.map((o) => {
          const active = o.value === value;
          return (
            <Chip
              key={o.value}
              size="small"
              color={active ? "primary" : "default"}
              variant={active ? "filled" : "outlined"}
              label={o.price != null ? `${o.value} min · ${o.isFrom ? "from " : ""}${cur} ${formatSlotPrice(o.price)}` : `${o.value} min`}
              onClick={() => onChange(o.value)}
              sx={{ cursor: "pointer" }}
            />
          );
        })}
      </Box>
    </Box>
  );
}

// Whole numbers drop the cents; fractional prices keep them.
const formatSlotPrice = (price) => {
  const n = parseFloat(price || 0);
  return Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2);
};

// The single price shared by every slot in every rendered group, or null when
// prices vary (peak pricing, mixed locations). When uniform, the day grid hides
// the per-cell price line — the duration picker already states it once.
export function getUniformGridPrice(groups) {
  let price = null;
  for (const g of groups || []) {
    if (g.minPrice !== g.maxPrice) return null;
    if (price === null) price = g.minPrice;
    else if (g.minPrice !== price) return null;
  }
  return price;
}

// The one pack nudge a whole set of slots earns.
//
// Unlike a class, a slot list carries no price of its own — the tiles hide it
// whenever the day agrees on one — so the hint belongs to the result set rather
// than to any tile. Courts price by branch and tier, so the loaded slots can
// resolve to several offers: the deepest saving is the one shown, marked
// `isFrom` when the others disagree or when some branch has no pack at all, the
// same way a varying slot price is prefixed "from".
export function resolveSlotSetOffer(slots, findPackageOffer) {
  const cheapestByKey = new Map();
  for (const slot of slots || []) {
    if (!slot?.offerKey) continue;
    const seen = cheapestByKey.get(slot.offerKey);
    if (!seen || slot.price < seen.price) cheapestByKey.set(slot.offerKey, slot);
  }

  const offers = [...cheapestByKey.values()].map(findPackageOffer).filter(Boolean);
  if (!offers.length) return null;

  const best = offers.reduce((a, b) => (b.saving > a.saving ? b : a));
  const isFrom = cheapestByKey.size > 1 && (offers.length < cheapestByKey.size || offers.some((o) => o.pricePerSession !== best.pricePerSession));
  return { ...best, isFrom };
}

// The concrete slot a group's "Any" option books. The server tags every slot in
// a time group with the recommended resource — the court the picker chose
// (courts via recommendedFacilityId) or the trainer (lessons via
// recommendedTrainerId). We resolve "Any" to that real slot here so checkout
// still sends a concrete facility/trainer (create re-checks it). Falls back to
// the first slot if no recommendation came through. The result carries
// `facilityAuto` — the client did not choose the court — so the booking is
// marked system-picked and stays re-organizable.
export function getRecommendedSlot(group) {
  const slots = group?.slots || [];
  const slot =
    slots.find((s) => s.recommendedFacilityId && s.facilityId === s.recommendedFacilityId) ||
    slots.find((s) => s.recommendedTrainerId && s.trainerId === s.recommendedTrainerId) ||
    slots[0] ||
    null;
  return slot ? { ...slot, facilityAuto: true } : null;
}

const DAY_NAMES = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

// The client's preferred time ranges that apply on `dateISO`. A range tagged
// "weekdays"/"weekend" applies to the matching day-type; a range tagged with a
// specific day name applies only on that day.
export function getApplicableTimeRanges(timeRanges, dateISO) {
  if (!Array.isArray(timeRanges) || !dateISO) return [];
  const wd = DateTime.fromISO(dateISO).weekday; // 1 = Monday … 7 = Sunday
  if (!wd) return [];
  const dayName = DAY_NAMES[wd - 1];
  const isWeekend = wd >= 6;
  return timeRanges.filter(
    (r) => r && r.from && r.to && (r.days === dayName || (r.days === "weekend" && isWeekend) || (r.days === "weekdays" && !isWeekend))
  );
}

// Keep the pre-grouped slot rows whose time falls inside the client's applicable
// preferred ranges for `dateISO`. Pure and cheap (string compares on "HH:mm") —
// meant to run inside a useMemo over already-loaded slots, no network involved.
export function filterGroupsByPreferredTime(groups, timeRanges, dateISO) {
  const ranges = getApplicableTimeRanges(timeRanges, dateISO);
  if (!ranges.length) return [];
  return (groups || []).filter((g) => ranges.some((r) => g.time >= r.from && g.time < r.to));
}

// Sport the Courts/Lessons views select when the URL carries none. Hardcoded to
// Tennis for now — the club's sport list order otherwise decides (it put Padel
// first once padel went online). To be replaced by a per-club default set in
// Partners. Falls back to the first sport when the club has no Tennis.
export function pickDefaultSport(availableSports = []) {
  return availableSports.find((s) => s.name?.trim().toLowerCase() === "tennis") || availableSports[0];
}

// Resolve the Sport + Service the "Use my preferences" button should select,
// from the client's preferences restricted to what's offered in this view. A
// preferred service pins its own sport (so the pair stays valid); otherwise the
// preferred sport is kept and the sport's first service is used. `hasPreference`
// is false when neither a preferred sport nor service is offered here — the
// caller hides the button in that case. Trainer/court extras stay per-view.
export function resolvePreferredSportService({
  sports = [],
  services = [],
  availableSports = [],
  categoryServices = [],
  currentSport,
  currentService
}) {
  const prefService = services.find((ps) => categoryServices.some((s) => s._id === ps.serviceId));
  const svc = prefService ? categoryServices.find((s) => s._id === prefService.serviceId) : null;
  const prefSport = sports.find((ps) => availableSports.some((s) => s._id === ps.sportId));
  const sportId = svc?.sportId || prefSport?.sportId || currentSport;
  const servicesForSport = categoryServices.filter((s) => !sportId || s.sportId === sportId);
  const serviceId = svc?._id || servicesForSport[0]?._id || currentService;
  return { sportId, serviceId, hasPreference: !!(prefSport || prefService) };
}

// Resolve the client's preferred location to apply via "Use my preferences",
// restricted to `serviceLocations` (the locations the about-to-be-selected
// service is actually offered at, already scoped to this view). Returns the
// first preferred location that's bookable here, or null — so it never points at
// a location the service isn't served at (which the coherence effect would
// immediately clear). Preferences are ordered, so the first match wins.
export function resolvePreferredLocationId(preferredLocations = [], serviceLocations = []) {
  const match = (preferredLocations || []).find((pl) => (serviceLocations || []).some((l) => l._id === pl.locationId));
  return match ? match.locationId : null;
}

// Duration options a service offers — each with a representative price — read
// straight from its pricing. Same source the server derives `filters.durations`
// from, and the same shape ({ value, label, price }), so the widget can populate
// the duration picker without an availability round-trip.
// With a `locationId`, the price mirrors what the server charges there: that
// location's override, falling back to the base price. Without one (catalogue
// pages, "All Locations"), it's the minimum of the base price and every location
// override, flagged `isFrom` when they differ.
export function getServiceDurationOptions(service, locationId) {
  const pricing = service?.pricing || {};
  const priceByDuration = new Map();
  for (const opt of Object.values(pricing)) {
    const dur = parseInt(opt?.base?.duration, 10);
    if (Number.isNaN(dur) || priceByDuration.has(dur)) continue;
    const basePrice = parseInt(opt?.base?.price, 10);

    if (locationId) {
      const override = parseInt(opt?.locations?.[locationId]?.price, 10);
      const resolved = !Number.isNaN(override) ? override : basePrice;
      priceByDuration.set(dur, { price: Number.isNaN(resolved) ? null : resolved, isFrom: false });
      continue;
    }

    const prices = [];
    if (!Number.isNaN(basePrice)) prices.push(basePrice);
    for (const loc of Object.values(opt?.locations || {})) {
      const locPrice = parseInt(loc?.price, 10);
      if (!Number.isNaN(locPrice)) prices.push(locPrice);
    }
    // isFrom: location overrides make this duration's price vary, so the
    // minimum shown should be prefixed "from".
    priceByDuration.set(dur, {
      price: prices.length ? Math.min(...prices) : null,
      isFrom: prices.length > 1 && Math.max(...prices) !== Math.min(...prices)
    });
  }
  return [...priceByDuration.keys()]
    .sort((a, b) => a - b)
    .map((d) => ({ value: d, label: `${d} min`, price: priceByDuration.get(d).price, isFrom: priceByDuration.get(d).isFrom }));
}

// Scroll-spy section id for the "Suggested for you" block, so the time-of-day
// nav can target it like any time bucket.
export const SUGGESTED_BUCKET_ID = "suggested";

// Horizontal time-of-day nav (mobile): one pill per non-empty bucket, plus a
// leading ✨ pill for "Suggested for you" when the client has matching slots.
// Keeps the active pill in view as the scroll-spy moves it, so scrolling the
// day scrolls the nav along with it.
export function TimeOfDayNav({ buckets, activeBucket, onBucketTap, suggestedCount = 0 }) {
  const stripRef = useRef(null);
  const chipRefs = useRef({});

  useEffect(() => {
    const strip = stripRef.current;
    const chip = chipRefs.current[activeBucket];
    if (!strip || !chip) return;
    // Center the active pill without touching vertical page scroll.
    const target = chip.offsetLeft - (strip.clientWidth - chip.offsetWidth) / 2;
    strip.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [activeBucket]);

  const pillSx = (active) => ({
    flexShrink: 0,
    px: 1.75,
    py: 0.65,
    borderRadius: "9999px",
    border: "1.5px solid",
    borderColor: active ? "primary.main" : "divider",
    bgcolor: active ? "primary.main" : "#fff",
    color: active ? "#fff" : "text.secondary",
    transition: "all 0.15s ease"
  });

  return (
    <Box ref={stripRef} sx={{ display: "flex", gap: 1, overflowX: "auto", "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none" }}>
      {suggestedCount > 0 && (
        <ButtonBase
          ref={(el) => {
            chipRefs.current[SUGGESTED_BUCKET_ID] = el;
          }}
          onClick={() => onBucketTap(SUGGESTED_BUCKET_ID)}
          aria-label="Suggested for you"
          sx={pillSx(activeBucket === SUGGESTED_BUCKET_ID)}
        >
          <AutoAwesomeIcon sx={{ fontSize: "0.9375rem", color: activeBucket === SUGGESTED_BUCKET_ID ? "inherit" : "primary.main" }} />
          <Typography component="span" sx={{ ml: 0.75, fontSize: "0.8rem", fontWeight: 600, color: "inherit", opacity: 0.85 }}>
            {suggestedCount}
          </Typography>
        </ButtonBase>
      )}
      {buckets.map((bucket) => {
        const active = bucket.id === activeBucket;
        return (
          <ButtonBase
            key={bucket.id}
            ref={(el) => {
              chipRefs.current[bucket.id] = el;
            }}
            onClick={() => onBucketTap(bucket.id)}
            sx={pillSx(active)}
          >
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "inherit" }}>
              {bucket.label}
              <Box component="span" sx={{ ml: 0.75, opacity: 0.7, fontWeight: 500 }}>
                {bucket.groups.length}
              </Box>
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

// Collapsed-filters toggle row (mobile): shows a one-line summary of the current
// selections and folds the filter grid open/closed, so the slot list gets the
// vertical space back.
export function MobileFilterFold({ open, onToggle, summary }) {
  return (
    <ButtonBase
      onClick={onToggle}
      aria-label={open ? "Hide filters" : "Show filters"}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.9,
        borderRadius: 2,
        border: "1.5px solid",
        borderColor: "divider",
        bgcolor: "#fff"
      }}
    >
      <TuneIcon sx={{ flexShrink: 0, fontSize: "1rem", color: "text.secondary" }} />
      <Typography
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          textAlign: "left",
          fontSize: "0.8rem",
          fontWeight: 600,
          color: "text.primary",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
      >
        {summary || "Filters"}
      </Typography>
      <KeyboardArrowDownIcon
        sx={{
          flexShrink: 0,
          fontSize: "1.125rem",
          color: "text.secondary",
          transform: open ? "rotate(180deg)" : "none",
          transition: "transform 0.15s ease"
        }}
      />
    </ButtonBase>
  );
}

// Closing edge of an open filter grid (mobile): a second way to fold it, at the
// bottom where the client's eye ends up after the last filter. The summary row
// that also folds it sits above the whole grid and was easy to miss.
export function MobileFilterFoldHandle({ onFold }) {
  return (
    <ButtonBase
      onClick={onFold}
      aria-label="Hide filters"
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1,
        mt: 1,
        py: 0.25,
        color: "text.secondary",
        "&::before, &::after": { content: '""', flex: 1, height: "1px", bgcolor: "divider" }
      }}
    >
      <Typography component="span" sx={{ display: "flex", alignItems: "center", fontSize: "0.75rem", fontWeight: 600 }}>
        Hide filters
        <KeyboardArrowUpIcon sx={{ fontSize: "1.125rem", ml: 0.25 }} />
      </Typography>
    </ButtonBase>
  );
}

// Compact search input for bottom sheets / option pickers. Not autofocused on
// purpose — the mobile keyboard only opens when the user asks for it.
export function SheetSearchField({ value, onChange, placeholder = "Search..." }) {
  return (
    <TextField
      fullWidth
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: "1.125rem" }} />
          </InputAdornment>
        ),
        sx: { borderRadius: 2 }
      }}
    />
  );
}

// A full-width, tappable filter field that opens a bottom sheet (mobile only).
export function FilterChip({ label, value, active, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: "100%",
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.9,
        borderRadius: 2,
        border: "1.5px solid",
        borderColor: active ? "primary.main" : "divider",
        bgcolor: (theme) => (active ? alpha(theme.palette.primary.main, 0.1) : "#fff"),
        transition: "all 0.15s ease"
      }}
    >
      {/* Label and value share a baseline: centred on its own, the small caps label rides
          higher than the value beside it. */}
      <Box component="span" sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "baseline", gap: 0.75 }}>
        <Typography
          component="span"
          sx={{
            flexShrink: 0,
            fontSize: "0.6rem",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "text.secondary"
          }}
        >
          {label}
        </Typography>
        <Typography
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            textAlign: "left",
            fontSize: "0.8rem",
            fontWeight: 600,
            color: active ? "primary.main" : "text.primary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {value}
        </Typography>
      </Box>
      <KeyboardArrowDownIcon sx={{ flexShrink: 0, fontSize: "1rem", color: "text.secondary" }} />
    </ButtonBase>
  );
}

// A single selectable row inside a bottom sheet / options popover.
export function OptionRow({ label, sublabel, selected, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        px: 2,
        py: 1.75,
        borderRadius: 2,
        border: "1.5px solid",
        borderColor: selected ? "primary.main" : "divider",
        bgcolor: (theme) => (selected ? alpha(theme.palette.primary.main, 0.08) : "#fff"),
        textAlign: "left"
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "0.95rem",
            fontWeight: 600,
            color: selected ? "primary.main" : "text.primary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {label}
        </Typography>
        {sublabel && (
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {sublabel}
          </Typography>
        )}
      </Box>
      {selected && <CheckIcon sx={{ fontSize: "1.25rem", color: "primary.main", flexShrink: 0 }} />}
    </ButtonBase>
  );
}
